import { Input } from '@crowdex/ui/components/input';
import { ChevronRight, LogOut, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  availableSections,
  type SettingsSection,
  searchSettings,
} from '@/lib/settings-page/sections';
import { SettingLinkRow, SettingsGroup } from './setting-controls';
import { ProfileAvatar, SECTION_ICONS, type SettingsNavigationModel } from './settings-navigation';

function Tile({ icon: Icon }: { icon: (typeof SECTION_ICONS)[SettingsSection] }) {
  return (
    <span className="flex size-8 items-center justify-center rounded-lg bg-control">
      <Icon aria-hidden className="size-4 text-foreground" />
    </span>
  );
}

/**
 * Settings on a phone (board 13): an iOS/Android grouped list, one sub-page per section, with
 * the profile first. What only applies on a computer is left out.
 */
export function SettingsMobileList({
  model,
  onSelect,
  onSignOut,
}: {
  model: SettingsNavigationModel;
  onSelect: (section: SettingsSection) => void;
  onSignOut: () => void;
}) {
  const [query, setQuery] = useState('');
  const sections = useMemo(
    () => availableSections({ phone: true, local: model.local }),
    [model.local],
  );
  const shown = searchSettings(sections, query);
  const personal = shown.filter(
    (section) => section.group === 'personal' && section.id !== 'account',
  );
  const organization = shown.filter((section) => section.group === 'organization');
  const plugins = shown.filter((section) => section.group === 'plugins');
  const account = shown.some((section) => section.id === 'account');
  return (
    <div className="mx-auto w-full max-w-xl px-4 pt-3 pb-8">
      <div className="mb-4 flex h-11 items-center gap-2.5 rounded-xl border bg-card px-3.5">
        <Search aria-hidden className="size-4 text-muted-foreground" />
        <Input
          appearance="document"
          aria-label="Search settings"
          className="min-w-0 flex-1 bg-transparent text-base text-foreground outline-none placeholder:text-muted-foreground"
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search settings"
          type="search"
          value={query}
        />
      </div>
      {account && (
        <button
          className="mb-5 flex w-full items-center gap-3.5 rounded-2xl border bg-card px-3.5 py-3 text-left"
          onClick={() => onSelect('account')}
          type="button"
        >
          <ProfileAvatar name={model.displayName} size="lg" />
          <span className="min-w-0 flex-1">
            <span className="block truncate font-semibold text-base text-foreground">
              {model.displayName}
            </span>
            <span className="block truncate text-muted-foreground text-sm">
              Account · Crowdex Cloud
            </span>
          </span>
          <ChevronRight aria-hidden className="size-5 text-subtle-foreground" />
        </button>
      )}
      {personal.length > 0 && (
        // The user's own preferences: "You", never "Personal" (the workspace's name).
        <SettingsGroup title="You">
          {personal.map((section) => (
            <SettingLinkRow
              key={section.id}
              leading={<Tile icon={SECTION_ICONS[section.id]} />}
              onSelect={() => onSelect(section.id)}
              title={section.title}
              value={model.details[section.id]}
            />
          ))}
        </SettingsGroup>
      )}
      {organization.length > 0 && (
        <SettingsGroup title="Organization">
          {organization.map((section) => (
            <SettingLinkRow
              key={section.id}
              leading={<Tile icon={SECTION_ICONS[section.id]} />}
              onSelect={() => onSelect(section.id)}
              title={section.title}
              value={model.details[section.id]}
            />
          ))}
        </SettingsGroup>
      )}
      {plugins.length > 0 && (
        <SettingsGroup title="Plugins & connections">
          {plugins.map((section) => (
            <SettingLinkRow
              key={section.id}
              leading={<Tile icon={SECTION_ICONS[section.id]} />}
              onSelect={() => onSelect(section.id)}
              title={section.title}
              value={
                section.id === 'connections' && model.reconnect > 0 ? (
                  <span className="flex items-center gap-2">
                    <span aria-hidden className="size-2 rounded-full bg-attention" />
                    {model.reconnect} to reconnect
                  </span>
                ) : undefined
              }
            />
          ))}
        </SettingsGroup>
      )}
      {shown.length === 0 && (
        <p className="px-1 py-2 text-muted-foreground text-sm">No settings found.</p>
      )}
      <button
        className="flex h-12 w-full items-center gap-3 rounded-2xl border bg-card px-4 text-base text-destructive"
        onClick={onSignOut}
        type="button"
      >
        <LogOut aria-hidden className="size-5 rotate-180" />
        Sign out
      </button>
      <p className="mt-3 text-center text-muted-foreground text-xs">{model.versionLine}</p>
    </div>
  );
}
